import { last } from 'lodash'

import i18n from '@/i18n/i18n'
import OrderService from '@/services/retailer/OrderService.js'
import country from '@/utilities/country'
import { build_query, errorMessage } from '@/utilities/helpers.js'

import { ORDER_ERROR_MESSAGES } from '@/constants/errorMessages'

const { t } = i18n.global

export const allowedFilters = [
  'id',
  'payments.status',
  'farmer_id',
  'farmer_name',
  'created_between',
  'completed_between',
  'payment_date_between',
  'workflow',
  'by_retailer_user',
  'application.company_id'
]
export const allowedIncludes = [
  'farmer',
  'farmer.address',
  'application.company',
  'payments',
  'retailer',
  'application.salesChannel',
  'application.dsPotentialFraud',
  'details',
  'retailerFastPayableOrder',
  'logoInvoice',
  'payments.collections',
  'payments.holderBank',
  'incomeProtectedSale'
]
export const allowedSorts = '-created_at'
export const allowedAppends = [
  'retailer_order_status',
  'can_be_deleted_by_retailer',
  'receivable_open_amount_with_fiba_commission',
  'forward_price_for_early_payment',
  'confirm_digital_promissory_note_document_workflow_status',
  'is_available_for_fast_retailer_payment',
  'can_farmer_pay_with_credit_card'
]

export const namespaced = true

export const state = {
  orders: null,
  order: {},
  meta: null,
  invoice: null,
  promissory_note: null,
  sales_contract: null,
  transportation_document: null,
  order_receipt: null,
  crop_equivalent_contract: null,
  repayment_schedule: null,
  credit_agreement: null,
  workflowJobs: null,
  invoice_pdf_url: null,
  promissory_note_pdf_url: null,
  promissory_note_and_order_receipt_pdf_url: null,
  order_receipt_pdf_url: null,
  crop_equivalent_contract_pdf_url: null,
  repayment_schedule_pdf_url: null,
  credit_agreement_pdf_url: null,
  credit_application_pdf_url: null,
  mortgage_contract_pdf_url: null,
  fiba_documents: {},
  sales_contract_pdf_url: null,
  transportation_document_pdf_url: null,
  farmer_transportation_document_pdf_url: null,
  isAnswerLoading: false,
  guarantor: null,
  retailerProductPrices: null
}

export const mutations = {
  SET_ORDERS(state, orders) {
    state.orders = orders.data
    state.meta = orders.meta
  },
  SET_ORDER(state, order) {
    state.order = order
  },
  SET_META(state, meta) {
    state.meta = meta
  },
  SET_INVOICE(state, invoice) {
    state.invoice = invoice
  },
  SET_PROMISSORY_NOTE(state, promissory_note) {
    state.promissory_note = promissory_note
  },
  SET_SALES_CONTRACT(state, sales_contract) {
    state.sales_contract = sales_contract
  },
  SET_TRANSPORTATION_DOCUMENT(state, transportation_document) {
    state.transportation_document = transportation_document
  },
  SET_ORDER_RECEIPT(state, order_receipt) {
    state.order_receipt = order_receipt
  },
  SET_ORDER_RECEIPT_PDF_URL(state, order_receipt_pdf_url) {
    state.order_receipt_pdf_url = order_receipt_pdf_url
  },
  SET_CROP_EQUIVALENT_CONTRACT(state, crop_equivalent_contract) {
    state.crop_equivalent_contract = crop_equivalent_contract
  },
  SET_CROP_EQUIVALENT_CONTRACT_PDF_URL(state, crop_equivalent_contract_pdf_url) {
    state.crop_equivalent_contract_pdf_url = crop_equivalent_contract_pdf_url
  },
  SET_REPAYMENT_SCHEDULE(state, repayment_schedule) {
    state.repayment_schedule = repayment_schedule
  },
  SET_RETAILER_PRODUCT_PRICES(state, prices) {
    state.retailerProductPrices = prices
  },
  SET_REPAYMENT_SCHEDULE_PDF_URL(state, repayment_schedule_pdf_url) {
    state.repayment_schedule_pdf_url = repayment_schedule_pdf_url
  },
  SET_CREDIT_AGREEMENT(state, credit_agreement) {
    state.credit_agreement = credit_agreement
  },
  SET_CREDIT_AGREEMENT_PDF_URL(state, credit_agreement_pdf_url) {
    state.credit_agreement_pdf_url = credit_agreement_pdf_url
  },
  SET_CREDIT_APPLICATION_PDF_URL(state, credit_application_pdf_url) {
    state.credit_application_pdf_url = credit_application_pdf_url
  },
  SET_ORDER_WORKFLOWS(state, workflowJobs) {
    state.workflowJobs = workflowJobs
  },
  SET_DELETED(state, order) {
    state.order.deleted_at = order.deleted_at
    state.order.cancellation_reason = order.cancellation_reason
  },
  SET_INVOICE_PDF_URL(state, invoice) {
    state.invoice_pdf_url = invoice
  },
  SET_PROMISSORY_NOTE_PDF_URL(state, promissory_note) {
    state.promissory_note_pdf_url = promissory_note
  },
  SET_FIBA_DOCUMENT_URL(state, params) {
    state.fiba_documents[params.type] = params.url
  },
  SET_SALES_CONTRACT_PDF_URL(state, sales_contract) {
    state.sales_contract_pdf_url = sales_contract
  },
  SET_TRANSPORTATION_DOCUMENT_PDF_URL(state, transportation_document) {
    state.transportation_document_pdf_url = transportation_document
  },
  SET_FARMER_TRANSPORTATION_DOCUMENT_PDF_URL(state, farmer_transportation_document) {
    state.farmer_transportation_document_pdf_url = farmer_transportation_document
  },
  SET_PROMISSORY_NOTE_AND_ORDER_RECEIPT_PDF(state, promissory_note_and_invoice_order_receipt_pdf) {
    state.promissory_note_and_order_receipt_pdf = promissory_note_and_invoice_order_receipt_pdf
  },
  SET_ANSWER_LOADING(state, isAnswerLoading) {
    state.isAnswerLoading = isAnswerLoading
  },
  SET_MORTGAGE_CONTRACT_ITEM(state, item) {
    state.order.mortgage_contract_items = state.order.mortgage_contract_items.map(mci => {
      if (mci.id === item.id) {
        return item
      }
      return mci
    })
  },
  SET_MORTGAGE_CONTRACT_PDF_URL(state, mortgage_contract) {
    state.mortgage_contract_pdf_url = mortgage_contract
  },
  SET_GUARANTOR(state, guarantor) {
    state.guarantor = guarantor
  }
}
export const getters = {
  getOrderById: state => id => {
    return state.orders ? state.orders.find(order => order.id === id) : false
  }
}

export const actions = {
  getOrders({ commit, dispatch }, query) {
    if (country.is('ro')) {
      allowedAppends.push('application.is_company_applicant', 'forward_price_for_early_payment')
    }

    let newQuery = build_query(query, allowedFilters, allowedIncludes, allowedSorts, allowedAppends)

    return new Promise((resolve, reject) => {
      OrderService.getOrders(newQuery)
        .then(response => {
          commit('SET_ORDERS', response)
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: ORDER_ERROR_MESSAGES.getOrdersError + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getOrder({ commit, dispatch }, id, refresh) {
    if (refresh === null && id == state.order.id) return state.order

    return new Promise((resolve, reject) => {
      OrderService.getOrder(id)
        .then(response => {
          commit('SET_ORDER', response.data)
          commit('SET_GUARANTOR', last(response.data.application.guarantors))
          commit('SET_META', response.meta)
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  resendOTP({ dispatch }, params) {
    return new Promise(resolve => {
      OrderService.resendOTP(params.id, params.phone, params.farmer_id, params.otp_type).then(response => {
        const notification = {
          type: 'success',
          message: t('store.retailer.order.confirmation_code_resent')
        }
        dispatch('notification/add', notification, {
          root: true
        })
        resolve(response.data)
      })
    })
  },
  getInvoice({ commit, dispatch }, id) {
    return new Promise((resolve, reject) => {
      commit('SET_INVOICE', null)
      OrderService.getInvoice(id)
        .then(response => {
          commit('SET_INVOICE', response.data.url)
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: ORDER_ERROR_MESSAGES.getInvoiceError + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getPromissoryNote({ commit, dispatch }, id) {
    commit('SET_PROMISSORY_NOTE', null)
    return new Promise((resolve, reject) => {
      OrderService.getPromissoryNote(id)
        .then(response => {
          commit('SET_PROMISSORY_NOTE', response.data.url)
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: ORDER_ERROR_MESSAGES.getPromissoryNoteError + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getSalesContract({ commit, dispatch }, id) {
    commit('SET_SALES_CONTRACT', null)
    return new Promise((resolve, reject) => {
      OrderService.getSalesContract(id)
        .then(response => {
          commit('SET_SALES_CONTRACT', response.data.url)
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: ORDER_ERROR_MESSAGES.getSalesContractError + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getTransportationDocument({ commit, dispatch }, id) {
    commit('SET_TRANSPORTATION_DOCUMENT', null)
    return new Promise((resolve, reject) => {
      OrderService.getTransportationDocument(id)
        .then(response => {
          commit('SET_TRANSPORTATION_DOCUMENT', response.data.url)
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: ORDER_ERROR_MESSAGES.getTransportationDocumentError + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getOrderReceipt({ commit, dispatch }, id) {
    commit('SET_ORDER_RECEIPT', null)
    return new Promise((resolve, reject) => {
      OrderService.getOrderReceipt(id)
        .then(response => {
          commit('SET_ORDER_RECEIPT', response.data.url)
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: ORDER_ERROR_MESSAGES.getOrderReceiptError + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getCropEquivalentContract({ commit, dispatch }, id) {
    commit('SET_CROP_EQUIVALENT_CONTRACT', null)
    return new Promise((resolve, reject) => {
      OrderService.getCropEquivalentContract(id)
        .then(response => {
          commit('SET_CROP_EQUIVALENT_CONTRACT', response.data.url)
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getRepaymentSchedule({ commit, dispatch }, id) {
    commit('SET_REPAYMENT_SCHEDULE', null)

    return new Promise((resolve, reject) => {
      OrderService.getRepaymentSchedule(id)
        .then(response => {
          commit('SET_REPAYMENT_SCHEDULE', response.data.url)
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getRetailerProductPrices({ commit, dispatch }, id) {
    commit('SET_RETAILER_PRODUCT_PRICES', null)

    return new Promise((resolve, reject) => {
      OrderService.getRetailerProductPrices(id)
        .then(response => {
          commit('SET_RETAILER_PRODUCT_PRICES', response.data)
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  updateRetailerProductPrices({ commit, dispatch }, data) {
    return new Promise((resolve, reject) => {
      OrderService.updateRetailerProductPrices(data.id, data.payload)
        .then(response => {
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getCreditAgreement({ commit, dispatch }, id) {
    commit('SET_CREDIT_AGREEMENT', null)

    return new Promise((resolve, reject) => {
      OrderService.getCreditAgreement(id)
        .then(response => {
          commit('SET_CREDIT_AGREEMENT', response.data.url)
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  setAnswerLoading({ commit, dispatch }, isLoading) {
    commit('SET_ANSWER_LOADING', isLoading)
  },
  viewInvoice({ commit, dispatch }, id) {
    return new Promise((resolve, reject) => {
      OrderService.viewInvoice(id)
        .then(response => {
          commit('SET_INVOICE_PDF_URL', response)
          resolve(response)
        })
        .catch(err => {
          commit('SET_INVOICE_PDF_URL', null)
          if (err.response.status === 404) {
            resolve(err)
          } else {
            const notification = {
              type: 'danger',
              message: ORDER_ERROR_MESSAGES.viewInvoiceError
            }
            dispatch('notification/add', notification, {
              root: true
            })
            reject(err)
          }
        })
    })
  },
  viewPromissoryNote({ commit, dispatch }, id) {
    return new Promise((resolve, reject) => {
      OrderService.viewPromissoryNote(id)
        .then(response => {
          commit('SET_PROMISSORY_NOTE_PDF_URL', response)
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: ORDER_ERROR_MESSAGES.viewPromissoryNoteError
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  viewSalesContract({ commit, dispatch }, id) {
    return new Promise((resolve, reject) => {
      OrderService.viewSalesContract(id)
        .then(response => {
          commit('SET_SALES_CONTRACT_PDF_URL', response)
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: ORDER_ERROR_MESSAGES.viewSalesContractError
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  viewTransportationDocument({ commit, dispatch }, id) {
    return new Promise((resolve, reject) => {
      OrderService.viewTransportationDocument(id)
        .then(response => {
          commit('SET_TRANSPORTATION_DOCUMENT_PDF_URL', response)
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: ORDER_ERROR_MESSAGES.viewTransportationDocumentError
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  viewFarmerTransportationDocument({ commit, dispatch }, id) {
    return new Promise((resolve, reject) => {
      OrderService.viewFarmerTransportationDocument(id)
        .then(response => {
          commit('SET_FARMER_TRANSPORTATION_DOCUMENT_PDF_URL', response)
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: ORDER_ERROR_MESSAGES.viewTransportationDocumentError
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  viewFibaDocument({ commit, dispatch }, params) {
    return new Promise((resolve, reject) => {
      OrderService.viewFibaDocument(params)
        .then(response => {
          commit('SET_FIBA_DOCUMENT_URL', {
            type: params.type,
            url: response
          })
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: 'Doküman alınırken bir sorun oluştu!'
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  viewOrderReceipt({ commit, dispatch }, id) {
    return new Promise((resolve, reject) => {
      OrderService.viewOrderReceipt(id)
        .then(response => {
          commit('SET_ORDER_RECEIPT_PDF_URL', response)
          resolve(response)
        })
        .catch(err => {
          commit('SET_ORDER_RECEIPT_PDF_URL', null)
          const notification = {
            type: 'danger',
            message: t('store.retailer.order.order_receipt_error') + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  viewCropEquivalentContract({ commit, dispatch }, id) {
    return new Promise((resolve, reject) => {
      OrderService.viewCropEquivalentContract(id)
        .then(response => {
          commit('SET_CROP_EQUIVALENT_CONTRACT_PDF_URL', response)
          resolve(response)
        })
        .catch(err => {
          commit('SET_CROP_EQUIVALENT_CONTRACT_PDF_URL', null)
          const notification = {
            type: 'danger',
            message: t('store.retailer.order.crop_equivalent_contract_error') + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  viewPromissoryNoteAndOrderReceipt({ commit, dispatch }, id) {
    return new Promise((resolve, reject) => {
      OrderService.viewPromissoryNoteAndOrderReceipt(id)
        .then(response => {
          commit('SET_PROMISSORY_NOTE_AND_ORDER_RECEIPT_PDF', response)
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: ORDER_ERROR_MESSAGES.getPromissoryNoteAndOrderReceipError
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  viewRepaymentSchedule({ commit, dispatch }, id) {
    return new Promise((resolve, reject) => {
      OrderService.viewRepaymentSchedule(id)
        .then(response => {
          let blob = new Blob([response], { type: 'application/pdf' })
          commit('SET_REPAYMENT_SCHEDULE_PDF_URL', window.URL.createObjectURL(blob))
          resolve(response)
        })
        .catch(err => {
          commit('SET_REPAYMENT_SCHEDULE_PDF_URL', null)
          const notification = {
            type: 'danger',
            message: t('store.retailer.order.repayment_schedule_error') + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  viewCreditAgreement({ commit, dispatch }, id) {
    return new Promise((resolve, reject) => {
      OrderService.viewCreditAgreement(id)
        .then(response => {
          let blob = new Blob([response], { type: 'application/pdf' })
          commit('SET_CREDIT_AGREEMENT_PDF_URL', window.URL.createObjectURL(blob))
          resolve(response)
        })
        .catch(err => {
          commit('SET_CREDIT_AGREEMENT_PDF_URL', null)
          const notification = {
            type: 'danger',
            message: t('store.retailer.order.credit_agreement_error') + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  viewCreditApplication({ commit, dispatch }, id) {
    return new Promise((resolve, reject) => {
      OrderService.viewCreditApplication(id)
        .then(response => {
          let blob = new Blob([response], { type: 'application/pdf' })
          commit('SET_CREDIT_APPLICATION_PDF_URL', window.URL.createObjectURL(blob))
          resolve(response)
        })
        .catch(err => {
          commit('SET_CREDIT_APPLICATION_PDF_URL', null)
          const notification = {
            type: 'danger',
            message: t('store.retailer.order.credit_application_error') + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getWorkflowJobs({ commit, dispatch }, id) {
    return new Promise((resolve, reject) => {
      OrderService.getWorkflowJobs(id)
        .then(response => {
          commit('SET_ORDER_WORKFLOWS', response.data)
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: ORDER_ERROR_MESSAGES.getWorkflowJobsError + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  createWorkflowJob({ dispatch }, params) {
    return new Promise((resolve, reject) => {
      OrderService.createWorkflowJob(params.orderId, params.workflowId, params)
        .then(response => {
          const notification = {
            type: 'success',
            message: t('store.retailer.order.element_updated', {
              element: params.name
            })
          }
          dispatch('notification/add', notification, {
            root: true
          })
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  delete({ dispatch, state, commit }, params) {
    return new Promise((resolve, reject) => {
      OrderService.destroy(state.order.id, params)
        .then(response => {
          commit('SET_DELETED', response.data)
          const notification = {
            type: 'warning',
            message: t('store.shared.deleted', {
              element: t('shared.common.selling')
            })
          }
          dispatch('notification/add', notification, {
            root: true
          })
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  saveMortgageContractItem({ commit, dispatch }, params) {
    return new Promise((resolve, reject) => {
      OrderService.saveMortgageContractItem(params.itemId, params.payload)
        .then(response => {
          commit('SET_MORTGAGE_CONTRACT_ITEM', response.data)

          const notification = {
            type: 'success',
            message: t('store.shared.updated', {
              element: t('views.retailer.orders.mortgage_contract_items.mortgage_contract_item')
            })
          }
          dispatch('notification/add', notification, {
            root: true
          })

          resolve(response)
        })
        .catch(error => {
          const notification = {
            type: 'danger',
            message: errorMessage(error)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  viewMortgageContractItemDocument({ commit, dispatch }, id) {
    return new Promise((resolve, reject) => {
      OrderService.viewMortgageContractItemDocument(id)
        .then(response => {
          let blob = new Blob([response], { type: 'application/pdf' })
          commit('SET_MORTGAGE_CONTRACT_PDF_URL', window.URL.createObjectURL(blob))
          resolve(response)
        })
        .catch(err => {
          commit('SET_MORTGAGE_CONTRACT_PDF_URL', null)
          const notification = {
            type: 'danger',
            message: t('store.retailer.order.mortgage_contract_error') + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  sendPaymentLink({ dispatch }, orderId) {
    return new Promise((resolve, reject) => {
      OrderService.sendPaymentLink(orderId)
        .then(response => {
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: t('views.retailer.orders.error_while_sending_sms') + ' ' + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  }
}
